<template>
  <div class="business-card">
    <div class="profile-header">
      <img :src="photo" alt="Profile Photo" class="profile-photo" />
      <div class="profile-info">
        <div class="name-container">
          <span class="code-comment">// Hello World! I am</span>
          <h1>{{ name }}</h1>
        </div>
        <ul class="positions-list">
          <li v-for="position in positions" :key="position">{{ position }}</li>
        </ul>
      </div>
    </div>

    <div class="full-width-block">
      <div class="code-line">
        <span class="code-keyword">const</span> spheres = [
        <div class="code-content spheres-list">
          <span v-for="(sphere, index) in spheres" :key="sphere" class="sphere-item">
            <span class="code-string">"{{ sphere }}"</span><span v-if="index < spheres.length - 1">,</span>
          </span>
        </div>
        ];
      </div>
    </div>

    <div class="full-width-block">
      <EducationBlock />
    </div>

    <div class="full-width-block">
      <a
        :href="cvUrl"
        :download="cvDownloadName"
        class="contact-button cv-download-button"
      >
        <span class="contact-button-prefix">→</span>
        <span class="contact-button-text">
          <span class="code-keyword">cv</span>:
          <span class="code-string">"Download PDF"</span>
        </span>
      </a>
    </div>

    <div class="full-width-block">
      <CompetenceTetris :competences="competences" />
    </div>

    <div class="full-width-block contact-block">
      <div class="contact-actions">
        <a
          v-if="telegram"
          :href="telegram.url"
          target="_blank"
          rel="noopener noreferrer"
          class="contact-button"
        >
          <span class="contact-button-prefix">→</span>
          <span class="contact-button-text">
            <span class="code-keyword">telegram</span>:
            <span class="code-string">"{{ telegram.handle }}"</span>
          </span>
        </a>
        <a
          v-if="linkedin"
          :href="linkedin.url"
          target="_blank"
          rel="noopener noreferrer"
          class="contact-button"
        >
          <span class="contact-button-prefix">→</span>
          <span class="contact-button-text">
            <span class="code-keyword">linkedin</span>:
            <span class="code-string">"Open profile"</span>
          </span>
        </a>
        <a
          v-if="goodreads"
          :href="goodreads.url"
          target="_blank"
          rel="noopener noreferrer"
          class="contact-button"
        >
          <span class="contact-button-prefix">→</span>
          <span class="contact-button-text">
            <span class="code-keyword">goodreads</span>:
            <span class="code-string">"Open profile"</span>
          </span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import CompetenceTetris from '../components/CompetenceTetris.vue';
import EducationBlock from '../components/EducationBlock.vue';
import { competences } from '../stores/competences';
import { profile } from '../stores/profile';

export default {
  name: 'HomePage',
  components: {
    CompetenceTetris,
    EducationBlock,
  },
  data() {
    return {
      photo: profile.photoUrl,
      name: profile.name,
      positions: profile.positions || [],
      spheres: profile.spheres || [],
      competences,
      telegram: profile.telegram,
      linkedin: profile.linkedin,
      goodreads: profile.goodreads,
      cvUrl: '/cv.pdf',
      cvDownloadName: `${profile.name.replace(/\s+/g, '-')}-CV.pdf`,
    };
  }
};
</script>

<style scoped>
.business-card {
  max-width: 900px;
  margin: 2rem auto;
  padding: 2rem;
  border-radius: 8px;
  background-color: #1e1e1e;
  color: #d4d4d4;
  font-family: 'Fira Code', 'Consolas', monospace;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  border: 1px solid #333;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.profile-header {
  display: flex;
  align-items: flex-start;
  gap: 3rem;
}

.profile-photo {
  flex-shrink: 0;
  width: 200px;
  height: 200px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid #0078d4;
  transition: transform 0.3s ease;
}

.profile-photo:hover {
  transform: scale(1.02);
}

.profile-info {
  flex: 1;
  text-align: left;
  padding-top: 0.25rem;
}

.full-width-block {
  width: 100%;
}

.name-container {
  margin-bottom: 1rem;
}

.code-comment {
  color: #6a9955;
  display: block;
  margin-bottom: 0.5rem;
}

.code-keyword {
  color: #569cd6;
}

h1 {
  margin: 0;
  color: #4ec9b0;
  font-size: 2.5rem;
}

.positions-list {
  margin: 0;
  padding-left: 1.25rem;
  list-style: disc;
  color: #b0b0b0;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.7;
}

.positions-list li {
  font-weight: 400;
}

.code-line {
  background-color: #2d2d2d;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  font-size: 0.85rem;
  line-height: 1.5;
  border: 1px solid #333;
}

.code-content {
  margin-left: 1rem;
  word-break: break-word;
}

.code-string {
  color: #ce9178;
}

.spheres-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.15rem 0.35rem;
}

.sphere-item {
  white-space: nowrap;
}

.contact-block {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.contact-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.contact-button {
  flex: 1;
  min-width: 12rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  background-color: #2d2d2d;
  border: 1px solid #333;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.contact-button:hover {
  background-color: #3d3d3d;
  border-color: #0078d4;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.contact-button-prefix {
  color: #6a9955;
  font-size: 0.9rem;
}

.contact-button-text {
  font-size: 0.85rem;
}

.cv-download-button {
  width: 100%;
  box-sizing: border-box;
}

@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500;600&display=swap');
</style>
